import { useState, useEffect } from 'react'
import { toast } from 'react-toastify'
import { ky, useAPI, useSWRConfig, useSWRMutation } from './client'
import withFallbackReportData from '../util/withFallbackReportData'

export function useAssignmentList({ searchTerm }) {
  const { data, ...rest } = useAPI('audit/reviews-list' + (searchTerm ? `?searchTerm=${searchTerm}` : ''), false, false)
  const { audits = [], assignments = [] } = data || {}
  return {
    audits: audits.map(({ plan, ...audit }) => ({ ...plan, ...audit })),
    assignments: assignments.map(({ plan, ...assignment }) => ({
      ...plan,
      ...assignment
    })),
    ...rest
  }
}

export async function createAudit(assignmentId) {
  if (!assignmentId) return null
  return ky.post('audit/create-review/' + assignmentId).json()
}

export async function submitAudit(reviewId) {
  if (!reviewId) return null
  return ky
    .post('audit/submit-review/' + reviewId)
    .json()
    .catch((e) => console.error(e))
}

export function useRemoteAudit(auditId) {
  const { data, error, loading, isValidating, mutate: swrMutate } = useAPI(auditId ? 'audit/review/' + auditId : null)
  let [requested, setRequested] = useState(false)

  const updateRemote = (data) => {
    ky.post('audit/review/' + auditId, { json: data })
      .then(() => swrMutate())
      .catch((e) => {
        console.error(e)
        swrMutate()
      })
      .finally(() => setRequested(false))
  }

  function mutate(delta) {
    if (!auditId) return

    const { criteriaId, actions, gradeId, comment, answer, completed } = delta

    if (criteriaId === 'summary') {
      swrMutate(
        {
          ...data,
          appendices: actions?.filter((a) => a.action !== 'remove'),
          completed
        },
        false
      )
    } else {
      const updateAnswer = (answer) => {
        const { questionId, index, answerId, gradeId, comment, value } = answer

        // Get question response index
        const isResponseToQuestion = (r) => (index ? questionId === r.questionId : questionId === r.questionId && r.index === index)
        const QuestionIndex = section.responseQuestion?.findIndex(isResponseToQuestion)

        // Set question
        let question = {}

        if (QuestionIndex >= 0) question = section.responseQuestion[QuestionIndex]
        if (index) question = { ...question, index }

        question = {
          ...question,
          questionId,
          answerId,
          gradeId,
          comment,
          value
        }

        // Construct responseQuestion
        const responseQuestion = section.responseQuestion || []
        if (QuestionIndex < 0) responseQuestion.splice(responseQuestion.length, 0, question)
        else responseQuestion.splice(QuestionIndex, 1, question)

        return responseQuestion
      }
      // Get section
      const index = data?.responses.findIndex((r) => r.criteriaId === criteriaId)
      const section = index >= 0 ? data.responses[index] : { criteriaId, reviewId: data.id }

      // response
      let response = {
        ...section,
        gradeId,
        comment
      }

      if (answer) {
        const responseQuestion = updateAnswer(answer)

        // Updated response
        response = { ...response, responseQuestion }
      }
      const responses = [...data.responses.slice(0, index), response, ...data.responses.slice(index + 1)]
      swrMutate({ ...data, responses }, false)
    }

    // Update on the db
    updateRemote({ sections: delta })
    setRequested(true)
  }

  return {
    audit: data || {},
    error,
    loading,
    isValidating,
    mutate,
    requested
  }
}

export async function getToolData() {
  try {
    const request = await ky.get('audit/module')
    return await request.json()
  } catch (error) {
    // Silence unauthorized calls for when we're unauthed.
    if (error.response.status === 401) return
    toast('Unable to view criteria. Please try again later.', {
      type: 'warning'
    })
    return {
      count: 0,
      rows: []
    }
  }
}

export async function getGrades(grades) {
  try {
    const res = await ky.get('audit/grades', {
      searchParams: {
        grades
      }
    })
    return await res.json()
  } catch (error) {
    // Silence unauthorized calls for when we're unauthed.
    if (error.response.status === 401) return
    toast('Unable to view Grades. Please try again later.', {
      type: 'warning'
    })
    return {
      count: 0,
      rows: []
    }
  }
}

export const getFurtherDataList = async (pagination) => {
  try {
    const grades = await getGrades(process.env.REACT_APP_GRADES)

    const request = await ky.get('audit/further', {
      searchParams: pagination,
      customErrorMessage: 'Unable to retrieve further data. Please try again later.',
      showErrorToast: true
    })
    const furtherData = await request.json()

    return {
      ...furtherData,
      grades
    }
  } catch (error) {
    return {
      count: 0,
      rows: []
    }
  }
}

export const useCriteriaData = () => {
  const { data } = useAPI('audit/module')
  return data
}

export const getStatusStackedArea = async () => {
  return await withFallbackReportData(({ toast }) => ky.get(`audit/status/stacked-area`, toast('Audit Status')).json())
}

export const getGradingStackedArea = async () => {
  return await withFallbackReportData(({ toast }) => ky.get(`audit/grading/stacked-area`, toast('Overall Grading')).json())
}

export const getGradingSummary = async ({ from, to, viewAll, section, filters, onlyOverAllGrade }) => {
  return await withFallbackReportData(({ toast }) =>
    ky
      .get('audit/grading/summary', {
        searchParams: {
          ...(from ? { from } : {}),
          ...(to ? { to } : {}),
          ...(viewAll ? { viewAll } : {}),
          ...(section ? { section } : {}),
          ...(filters ? { filters: JSON.stringify(filters) } : {}),
          onlyOverAllGrade: onlyOverAllGrade ?? false
        },
        ...toast('Grading by Section')
      })
      .json()
  )
}

export const getGradingSummaryCSV = async ({ from, to, viewAll, section, filters }) => {
  const response = await ky
    .get('audit/grading/summary-csv', {
      searchParams: {
        ...(from ? { from } : {}),
        ...(to ? { to } : {}),
        ...(viewAll ? { viewAll } : {}),
        ...(section ? { section } : {}),
        ...(filters ? { filters: JSON.stringify(filters) } : {})
      },
      timeout: 30000
    })
    .blob()

  const url = window.URL.createObjectURL(response)

  const link = document.createElement('a')
  link.href = url

  link.setAttribute('download', `Grading_Summary_${new Date().getTime()}.csv`)

  document.body.appendChild(link)
  link.click()

  link.parentNode.removeChild(link)
  window.URL.revokeObjectURL(url)

  return response
}

const getUpdateCommentRoute = ({ auditId, criteriaId }) => `audit/review/${auditId}/criteria/${criteriaId}/comment`

const updateCommentSWR = async (key, { arg: { comment } }) =>
  await ky
    .post(key, {
      json: { comment }
    })
    .json()

export const useUpdateCriteriaCommentMutation = ({ auditId, criteriaId, onSuccess }) => {
  const { mutate } = useSWRConfig()

  const optimisticAuditUpdate = async (comment) => {
    await mutate(
      'audit/review/' + auditId,
      (prevAudit) => ({
        ...prevAudit,
        responses: prevAudit.responses?.map((r) => (r.criteriaId === criteriaId ? { ...r, comment } : r))
      }),
      { revalidate: false } // Don't refetch, as triggers loading
    )
  }

  const fetcher = async (key, { arg: comment }) => {
    optimisticAuditUpdate(comment)
    try {
      return updateCommentSWR(key, { arg: { auditId, criteriaId, comment } })
    } catch (error) {
      // Revert to server state on error
      await mutate('audit/review/' + auditId)
      throw error
    }
  }

  const context = useSWRMutation(getUpdateCommentRoute({ auditId, criteriaId }), fetcher, {
    onSuccess
  })

  return {
    updateCriteriaComment: context.trigger,
    ...context
  }
}

export function useCriteria(options) {
  const [data, setData] = useState([])
  const [isLoading, setIsLoading] = useState([])
  const searchParams = new URLSearchParams(options).toString()
  const key = 'audit/criteria'

  const get = async () => {
    try {
      setIsLoading(true)
      setData(await ky.get(searchParams ? `${key}?${searchParams}` : key, {}).json())
      setIsLoading(false)
    } catch (e) {
      setData([])
      setIsLoading(false)
    }
  }

  useEffect(() => {
    get()
  }, [])

  return { data, isLoading }
}

export const getExportFurtherAudits = async (params) => {
  const response = await ky
    .get('audit/further/export', {
      searchParams: params,
      timeout: 30000
    })
    .blob()

  const url = window.URL.createObjectURL(response)

  const link = document.createElement('a')
  link.href = url

  link.setAttribute('download', `Further_Data_export${new Date().getTime()}.csv`)

  document.body.appendChild(link)
  link.click()

  link.parentNode.removeChild(link)
  window.URL.revokeObjectURL(url)

  return response
}

export const updateAuditDeadline = async (assignmentId, deadlineDate) => {
  const body = { assignmentId, deadlineDate }

  const response = await ky.put('audit/deadline', {
    json: body,
    timeout: 30000
  })
  return response
}
